import { MapPin, User, CheckCircle, FlaskConical, ChevronRight, Upload, Eye, FileText, X, Download, SendHorizonal } from "lucide-react";
import { useState, useRef } from "react";
import { LabtestOrderDetails, LabtestOrderStatus } from "../../services/api/LaborderService/types";
import { PaymentStatus, PrescriptionDetail } from "../../services/api/OrderService/types";
import { ViewPrescription } from "../Inventory-Stocks & Orders/Clinic/ClinicOrder/OnlineOrder/PrescriptionView";

interface UploadedFile {
    id: number;
    file: File;
    name: string;
    size: number;
    type: string;
    url: string;
    uploadedAt: string;
}

const LabOrderDetailsView: React.FC<{
    selectedOrder: LabtestOrderDetails | null;
    orderLoading: boolean;
    uploadedFile:UploadedFile[];
    setUploadedFile:React.Dispatch<React.SetStateAction<UploadedFile[]>>;
    submitting: boolean;
    handleSubmitReport: (id: number) => void;
}> = ({ selectedOrder, orderLoading, uploadedFile,setUploadedFile,submitting,handleSubmitReport }) => {
    const [previewFile, setPreviewFile] = useState<UploadedFile | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedPrescription, setSelectedPrescription] = useState<PrescriptionDetail[]>([]);

    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return "";
        return new Date(dateString).toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatTime = (dateString: string | undefined) => {
        if (!dateString) return "";
        return new Date(dateString).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        const newFiles: UploadedFile[] = files.map((file) => ({
            id: Date.now() + Math.random(),
            file: file,
            name: file.name,
            size: file.size,
            type: file.type,
            url: URL.createObjectURL(file),
            uploadedAt: new Date().toISOString()
        }));
        setUploadedFile(prev => [...prev, ...newFiles]);
    };

    const handleRemoveFile = (fileId: number) => {
        setUploadedFile(prev => {
            const fileToRemove = prev.find(f => f.id === fileId);
            if (fileToRemove) {
                URL.revokeObjectURL(fileToRemove.url);
            }
            return prev.filter(f => f.id !== fileId);
        });
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const isImageFile = (type: string) => {
        return type.startsWith('image/');
    };

    const isPdfFile = (type: string) => {
        return type === 'application/pdf';
    };

    if (orderLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                    Loading order details...
                </div>
            </div>
        );
    }

    if (!selectedOrder) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                    Select an Labtest order to view details
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 border-l border-gray-400/30 h-full w-full overflow-y-auto hiddenScrollbar">
            <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <span className="text-gray-600 text-md font-semibold">Order ID: </span>
                        <span className="text-gray-900 font-medium">
                            {selectedOrder.orderNumber}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            accept="image/*,.pdf"
                            multiple
                            className="hidden"
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="bg-green-600 text-white px-4 py-2 text-sm rounded-md hover:bg-green-700 flex items-center transition-colors"
                        >
                            <Upload size={16} className="mr-2" /> Upload Report
                        </button>
                    </div>
                </div>
                {uploadedFile.length > 0 && (
                    <div className="pb-4 mb-4">
                        <h4 className="text-gray-600 text-md font-semibold mb-3 border-b border-gray-400/30 pb-2">
                            Uploaded Reports ({uploadedFile.length})
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {uploadedFile.map((file) => (
                                <div key={file.id} className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow">
                                    <div className="flex items-start gap-3">
                                        {/* File Preview Thumbnail */}
                                        <div className="w-12 h-12 bg-gray-100 rounded flex-shrink-0 flex items-center justify-center overflow-hidden">
                                            {isImageFile(file.type) ? (
                                                <img src={file.url} alt={file.name} className="w-full h-full object-cover" />
                                            ) : isPdfFile(file.type) ? (
                                                <FileText className="text-red-500" size={24} />
                                            ) : (
                                                <FileText className="text-gray-400" size={24} />
                                            )}
                                        </div>

                                        {/* File Info */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate" title={file.name}>
                                                {file.name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {formatFileSize(file.size)}
                                            </p>
                                            <p className="text-xs text-gray-400">
                                                {formatDate(file.uploadedAt)} {formatTime(file.uploadedAt)}
                                            </p>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => setPreviewFile(file)}
                                                className="p-1.5 hover:bg-blue-50 rounded transition-colors"
                                                title="Preview"
                                            >
                                                <Eye size={16} className="text-blue-600" />
                                            </button>
                                            <button
                                                onClick={() => handleRemoveFile(file.id)}
                                                className="p-1.5 hover:bg-red-50 rounded transition-colors"
                                                title="Remove"
                                            >
                                                <X size={16} className="text-red-600" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="pb-4">
                    <h4 className="text-gray-600 text-md font-semibold mb-3 border-b border-gray-400/30">Patient Details</h4>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                            {selectedOrder.patientDetails?.patientProfileImage ? (
                                <img
                                    src={selectedOrder.patientDetails?.patientProfileImage}
                                    alt={selectedOrder.patientDetails?.patientName}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gray-300">
                                    <User size={16} className="text-gray-500" />
                                </div>
                            )}
                        </div>
                        <div>
                            <p className="font-medium text-gray-900 text-sm">
                                {selectedOrder.patientDetails?.patientName}
                            </p>
                            <p className="text-xs text-gray-600">
                                +91 {selectedOrder.patientDetails?.patientMobileNumber}
                            </p>
                            <p className="text-xs text-gray-600">{selectedOrder?.patientDetails.patientAddress}{", "}{selectedOrder?.patientDetails.patientPincode}</p>
                        </div>
                    </div>
                </div>

                {/* Order Details Header */}
                <h3 className="text-gray-600 text-md font-semibold border-b border-gray-400/30">Tests Details</h3>

                {/* Main Content Layout */}
                <div className="flex gap-8">
                    {/* Left Side - Products */}
                    <div className="flex-1">
                        <div className="">
                            <div className="flex items-center gap-4">
                                <div className="flex-1">
                                    <h5 className="font-medium text-gray-600 text-sm mb-1">Sample Type : {selectedOrder.labtestItemsDetails?.labTestDetails?.sampleType}</h5>
                                </div>
                                <div>
                                    {selectedOrder.hasPrescription && (
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-900 font-medium text-sm">
                                                    Prescriptions :
                                                </span>
                                                <button
                                                    onClick={() =>
                                                        setSelectedPrescription(
                                                            selectedOrder?.labtestPrescriptionDetails || []
                                                        )
                                                    }
                                                    className="text-cyan-500 hover:underline text-sm font-medium"
                                                >
                                                    View
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center pr-4">
                        <h3 className="text-md font-semibold text-gray-600 pb-2">{selectedOrder.labtestItemsDetails?.labTestDetails?.name}</h3>
                        <h4 className="ml-auto text-xs bg-blue-500 text-white px-3 py-1 rounded-full">{selectedOrder.labtestItemsDetails?.labTestDetails?.includedTests} tests</h4>
                    </div>

                    {/* Group Lab Tests */}
                    {selectedOrder?.labtestItemsDetails?.labTestDetails?.includedGroupLabtests && selectedOrder?.labtestItemsDetails?.labTestDetails?.includedGroupLabtests.length > 0 && selectedOrder?.labtestItemsDetails?.labTestDetails?.includedGroupLabtests?.map((test) => (
                        <div key={test.id} className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <h4 className="font-semibold text-blue-900">{test.name}</h4>
                                {test.tests?.length > 0 && (
                                    <span className="ml-auto text-xs bg-blue-500 text-white px-2 py-1 rounded-full">
                                        {test.tests.length} tests
                                    </span>
                                )}
                            </div>
                            {test.tests?.length > 0 && (
                                <div className="ml-4 space-y-2 ">
                                    {test.tests.map((item) => (
                                        <div key={item.id} className="flex items-center gap-2 text-sm text-gray-700">
                                            <ChevronRight className="text-blue-500" size={16} />
                                            {item.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Single Lab Tests */}
                    {selectedOrder?.labtestItemsDetails?.labTestDetails?.includedSingleLabtests && selectedOrder?.labtestItemsDetails?.labTestDetails?.includedSingleLabtests.length > 0 && (
                        <div className="space-y-2">
                            {selectedOrder.labtestItemsDetails.labTestDetails.includedSingleLabtests.map((test) => (
                                <div key={test.id} className="bg-blue-50 rounded-lg p-3 border border-blue-200 flex items-center gap-2">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                    <span className="text-blue-800 font-semibold">{test.name}</span>
                                    <span className="ml-auto text-xs bg-blue-500 text-white px-2 py-1 rounded-full">
                                        1 test
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Bottom Section */}
                <div className="flex justify-between items-center py-2">
                    <div>
                        <h4 className="text-gray-600 text-md font-semibold mb-2">Picked by</h4>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                                {selectedOrder.deliveryPersonDetails?.deliveryPersonProfileImage ? (
                                    <img
                                        src={selectedOrder.deliveryPersonDetails?.deliveryPersonProfileImage}
                                        alt={selectedOrder.deliveryPersonDetails?.deliveryPersonName}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-300">
                                        <User size={16} className="text-gray-500" />
                                    </div>
                                )}
                            </div>
                            <div>
                                <p className="font-medium text-gray-900 text-sm">
                                    {selectedOrder.deliveryPersonDetails?.deliveryPersonName}
                                </p>
                                <p className="text-xs text-gray-600">
                                    +91 {selectedOrder.deliveryPersonDetails?.deliveryPersonMobileNumber}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {formatDate(selectedOrder.labtestTimelineDetails.find((item) => item.timelineStatus === LabtestOrderStatus.PICKUP_COMPLETED)?.timelineTimestamp)},{" "}
                                    {formatTime(selectedOrder.labtestTimelineDetails.find((item) => item.timelineStatus === LabtestOrderStatus.PICKUP_COMPLETED)?.timelineTimestamp)}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="my-4">
                        <div className="flex items-start gap-2 mb-2">
                            <span className="text-gray-600 text-md font-semibold ">
                                Pickup Address
                            </span>
                        </div>
                        <div className="text-gray-700 text-sm">
                            <span className="flex flex-row items-center gap-2">
                                <MapPin size={16} className="text-red-500" />
                                <p>{selectedOrder.orderAddress}</p>
                            </span>

                            <p>Pin Code: {selectedOrder.orderPincode}</p>
                        </div>
                    </div>
                </div>
                <div className="w-full flex justify-between items-center">
                    {/* Payment and items count */}
                    <div className="border my-8 border-[#DBDBDB] rounded-md shadow-sm w-[40%]">
                        <div className="flex items-center gap-2 p-2 border-b border-gray-400/30">
                            <FlaskConical size={16} className="text-gray-600" />
                        </div>
                        <div className="flex items-center justify-between p-2">
                            <div className="">
                                <p className="text-sm text-gray-700 mb-1">Total payable</p>
                                <p className="text-xl font-bold text-gray-900">
                                    {selectedOrder.orderTotal}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle size={16} className="text-green-600" />
                                <span className="text-sm text-green-600 font-medium">
                                    {selectedOrder.paymentStatus === PaymentStatus.SUCCESS
                                        ? "Payment Approved"
                                        : "Payment Pending"}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div>
                        {uploadedFile.length > 0 && (
                            <button className="bg-green-600 text-white px-4 py-2 text-sm rounded-md hover:bg-green-700 flex items-center transition-colors gap-3" disabled={submitting} onClick={()=>handleSubmitReport(selectedOrder?.id)}> Submit Report  <SendHorizonal size={12} /></button>
                        )}
                    </div>
                </div>
            </div>
            {selectedPrescription.length > 0 && (
                <ViewPrescription
                    prescription={selectedPrescription}
                    onClose={() => setSelectedPrescription([])}
                />
            )}

            {previewFile && (
                <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-4 border-b">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">{previewFile.name}</h3>
                                <p className="text-sm text-gray-500">{formatFileSize(previewFile.size)}</p>
                            </div>
                            <div className="flex gap-2">
                                <a
                                    href={previewFile.url}
                                    download={previewFile.name}
                                    className="p-2 hover:bg-gray-100 rounded transition-colors"
                                    title="Download"
                                >
                                    <Download size={20} className="text-gray-600" />
                                </a>
                                <button
                                    onClick={() => setPreviewFile(null)}
                                    className="p-2 hover:bg-gray-100 rounded transition-colors"
                                >
                                    <X size={20} className="text-gray-600" />
                                </button>
                            </div>
                        </div>

                        {/* Modal Content */}
                        <div className="flex-1 overflow-auto p-4 bg-gray-50">
                            {isImageFile(previewFile.type) ? (
                                <img
                                    src={previewFile.url}
                                    alt={previewFile.name}
                                    className="max-w-full h-auto mx-auto"
                                />
                            ) : isPdfFile(previewFile.type) ? (
                                <iframe
                                    src={previewFile.url}
                                    className="w-full h-full min-h-[700px]"
                                    title={previewFile.name}
                                />
                            ) : (
                                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                                    <FileText size={48} className="mb-4" />
                                    <p>Preview not available for this file type</p>
                                    <a
                                        href={previewFile.url}
                                        download={previewFile.name}
                                        className="mt-4 text-blue-600 hover:underline"
                                    >
                                        Download file
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LabOrderDetailsView;